import { Component, Input, OnInit, inject } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { FuelKpiRequest } from "../../../../_shared";
import { BaseWidgetComponent } from '../../../../../home/home-widgets/widgets/base-widget.component';
import { formatCost, formatCostDifference, formatDropdownValue, getEnumFromString } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { AuthService, FilterService } from '../../../../../_shared/services';
import { ReconciliationVariateService } from '../../../services/reconciliation-variate.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { isUptrendPositive } from '../../../../../_shared/helpers/kpiWidget/dropdown-measurement.helper';
import { CurrencyHelper } from '../../../../../_shared/helpers/currency/currency-map.helper';
import { DropdownOption, KpiWidgetComponent } from '../../../../../_shared/components/kpi-widget/kpi-widget.component';
import { MeasurementTypes } from '../../../../../_shared/enums/kpiStats/measurement-types.enum';

@Component({
  selector: 'app-reconciliation-variant-widget',
  standalone: true,
  imports: [KpiWidgetComponent, TranslocoPipe],
  templateUrl: './reconciliation-variant-widget.component.html',
  styleUrl: './reconciliation-variant-widget.component.scss'
})
export class ReconciliationVariantWidgetComponent extends BaseWidgetComponent implements OnInit {

  @Input() selectedFilter: string;

  private transloco = inject(TranslocoService);

  icon = 'assets/icons/icon-reconciliation-variate.svg';
  cost: number;
  differenceChange: number;
  hasError = false;
  errorMessage: string = "";
  currencyCode: string = '';
  currencySymbol: string = '';
  selectedUnit = MeasurementTypes.Volume.toString();
  dropdownOptions: DropdownOption[] = [];

  constructor(
    protected override filterService: FilterService,
    private reconciliationVariateService: ReconciliationVariateService,
    private errorHandlerService: ErrorHandlerService,
    private authService: AuthService
  ) {
    super(filterService);
  }

  ngOnInit(): void {
    this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR'
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);

    this.dropdownOptions = [
      { label: this.transloco.translate('fuelDashboard.units.litres'), value: MeasurementTypes.Volume.toString(), unit: 'ℓ' },
      { label: this.transloco.translate('fuelDashboard.units.percentage'), value: MeasurementTypes.Percentage.toString(), unit: '%' },
      { label: this.transloco.translate('fuelDashboard.units.cost', { currency: this.currencyCode }), value: MeasurementTypes.CostPerUnit.toString(), unit: '' },
    ];
  }

  protected override async onFilterChangeAsync(): Promise<void> {
    await this.loadReconciliationData();
  }

  public async onDropdownChanged(selectedOption: DropdownOption) {
    this.selectedUnit = selectedOption.value;
    this.isLoading = true;
    await this.loadReconciliationData();
  }

  public formatValue(value: number): string {
    if (this.selectedUnit === MeasurementTypes.CostPerUnit.toString()) {
      return formatCost(value, this.currencySymbol);
    }

    return formatDropdownValue(value, this.selectedUnit, this.dropdownOptions, getEnumFromString);
  }

  public formatCost(value: number): string {
    return formatCost(value, this.currencySymbol);
  }

  public formatCostDifference(value: number): string {
    return formatCostDifference(value, this.selectedFilter, this.currencySymbol, (key, params) => this.transloco.translate(key, params));
  }

  get isUptrendPositive(): boolean {
    return isUptrendPositive(getEnumFromString(this.selectedUnit));
  }

  private async loadReconciliationData(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;

    try {
      const request: FuelKpiRequest = {
        fuelMeasurementType: getEnumFromString(this.selectedUnit),
        accountIds: this.widgetFilter.accountIds,
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!,
      };

      this.cancelSubject.next();
      const response = await this.reconciliationVariateService.getReconciliationVariateSummary(request, this.cancelSubject);

      if (response.data) {
        const data = response.data;
        this.value = data.totalValue;
        this.percentageDifference = data.percentageChange;
        this.cost = data.totalCost;
        this.difference = data.difference;
        this.differenceChange = data.percentageChange;
      }

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: (message) => {
          this.errorMessage = message;
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }
}